import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiFetch';

export type ValueCatalogSlug =
  | 'grade-bands' | 'curricula' | 'languages' | 'teaching-modes' | 'accepted-levels';

export interface ValueCatalogEntry {
  id: number;
  code: string;
  label: string;
  sortOrder: number;
}

async function fetchValueCatalog(slug: ValueCatalogSlug): Promise<ValueCatalogEntry[]> {
  const res = await apiFetch(`/api/v1/catalog/values/${slug}`);
  if (!res.ok) throw new Error(`Value catalog fetch failed: ${slug} (${res.status})`);
  return res.json();
}

/**
 * Catalogs are admin-managed and append-only (ADR-072, never deleted) — safe to
 * fetch once per session (staleTime: Infinity), unlike useFilterOptions's live-derived data.
 */
export function useValueCatalog(slug: ValueCatalogSlug) {
  const query = useQuery<ValueCatalogEntry[]>({
    queryKey: ['value-catalog', slug],
    queryFn: () => fetchValueCatalog(slug),
    staleTime: Infinity,
  });

  const entries = query.data ?? [];

  function labelOf(code: string): string {
    return entries.find(e => e.code === code)?.label ?? code.replace(/_/g, ' ');
  }

  return {
    entries,
    isLoading: query.isLoading,
    isError: query.isError,
    labelOf,
  };
}

export function useGradeBands() { return useValueCatalog('grade-bands'); }
export function useCurricula() { return useValueCatalog('curricula'); }
export function useLanguages() { return useValueCatalog('languages'); }
export function useTeachingModes() { return useValueCatalog('teaching-modes'); }
export function useAcceptedLevels() { return useValueCatalog('accepted-levels'); }
